import { Device } from "@capacitor/device";
import { IonAlert, IonContent, IonPage } from "@ionic/react";
import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import LimeAPI from "../classes/LimeAPI";
import buildInfo from "../build-info.json";
import ContactInfo from "../components/ContactInfo/ContactInfo";
import Login from "../components/Login/Login";

interface PageLoginProps extends RouteComponentProps {}

const PageLogin: React.FC<PageLoginProps> = ({ history }) => {
  const [buildDate, setBuildDate] = useState<string>("");
  const [platform, setPlatform] = useState<string>("");
  const [os, setOs] = useState<string>("");

  const [clickCount, setClickCount] = useState<number>(0);
  const REQUIRED_CLICKS = 30;
  const touchHandledRef = useRef<boolean>(false);

  /**
   * Hidden Easter Egg Code
   * More information about how this code works and why it exists in the README.md file
   */
  useEffect(() => {
    setBuildDate(buildInfo.buildDate);

    const getPlatformInfo = async () => {
      const info = await Device.getInfo();
      setPlatform(info.platform);
      setOs(info.operatingSystem);
    };

    getPlatformInfo();
  }, []);

  const handleClick = (e: React.MouseEvent<any> | React.TouchEvent<any>) => {
    // On iOS, touch events trigger both touch and click
    // Ignore click events that follow touch events to prevent double counting
    if ("touches" in e) {
      // This is a touch event
      touchHandledRef.current = true;
      // Reset the flag after a delay to allow click events to be ignored
      setTimeout(() => {
        touchHandledRef.current = false;
      }, 300);
    } else {
      // This is a click event - check if it was preceded by a touch event
      if (touchHandledRef.current) {
        // Ignore this click event as it was already handled as a touch
        return;
      }
    }

    const newCount = clickCount + 1;

    if (newCount >= REQUIRED_CLICKS) {
      history.push("/login-review-test-for-ios-and-android");
      setClickCount(0);
    } else {
      setClickCount(newCount);
    }
  };

  const [showBannerPopup, setShowBannerPopup] = useState(false);
  const isMobile = useMemo(() => ["ios", "android"].includes(os), [os]);

  useEffect(() => {
    if (platform === "web" && isMobile) {
      setShowBannerPopup(true);
    }
  }, [platform, isMobile]);

  const handleCloseBannerPopup = () => {
    setShowBannerPopup(false);
  };

  const renderBannerPopup = () => {
    if (!showBannerPopup) return null;

    const buttons: Array<{ text: string; role?: string; handler: () => void }> =
      [
        {
          text: "Stäng",
          role: "cancel",
          handler: handleCloseBannerPopup,
        },
      ];

    if (["ios", "android"].includes(os)) {
      const buttonHref =
        os === "ios"
          ? "https://apps.apple.com/se/app/byggkeramikr%C3%A5det/id1503332901"
          : "https://play.google.com/store/apps/details?id=se.bkr&hl=sv";
      const buttonText = os === "ios" ? "Till App Store" : "Till Google Play";

      buttons.push({
        text: buttonText,
        handler: () => {
          window.open(buttonHref, "_blank");
          handleCloseBannerPopup();
        },
      });
    }

    return (
      <div style={{ minWidth: 350, maxWidth: 420, margin: "0 auto" }}>
        <IonAlert
          isOpen={showBannerPopup}
          onDidDismiss={handleCloseBannerPopup}
          header="Tips!"
          message="För att få bästa upplevelsen av BKR's app, använd gärna App-versionen istället."
          buttons={buttons}
          cssClass="wider-alert"
        />
      </div>
    );
  };

  const debugTextStyle: CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: "0.25rem",
    padding: "0.25rem",
    textAlign: "right",
    fontSize: "8px",
    color: "#999999",
    zIndex: 9999,
  };

  return (
    <IonPage>
      <IonContent className="ion-no-padding">
        <div style={debugTextStyle}>
          {`${platform} - ${os} - ${buildDate}`}
        </div>
        {renderBannerPopup()}
        <Login onLogoClick={handleClick} apiUrl={LimeAPI.apiUrl || "(ej satt)"} />
        <ContactInfo extraClasses="ion-text-center contact-info-small" />
      </IonContent>
    </IonPage>
  );
};

export default withRouter(PageLogin);
